import React, { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import { createExercise, createNote, getTrainerLocations, updateWorkout } from '../services/api';
import { buildApiUrl } from '../config/api';
import { getApiAccessToken } from '../config/authToken';
const WorkoutDetailModal = lazy(() => import('./WorkoutDetailModal'));
import OptimizedWorkoutForm from './OptimizedWorkoutForm';
import PlannerView from './PlannerView';
import ClassicWeekPlannerCompact from './ClassicWeekPlannerCompact';
import { LoadingSpinner, LoadingOverlay, LoadingCard, useLoading } from './LoadingSpinner';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { DatePicker } from './ui/date-picker';
import { Empty, EmptyIcon, EmptyTitle, EmptyDescription, EmptyAction } from './ui/empty';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Loader2, Plus, Dumbbell, Wallet, MoreVertical } from 'lucide-react';
import HorizontalDatePicker from './HorizontalDatePicker';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { isWorkoutDebited } from '../utils/workoutDebit';
import { useI18n } from '../i18n/context';
import { getIntlLocaleTag } from '../lib/localeRuntime';

/** Apply mad:clientUpdated-style fields to a single workout row (list + group). */
function applyClientDisplayPatch(workout, clientId, patch) {
  if (!workout || !clientId || !patch || Object.keys(patch).length === 0) return workout;

  const mergeOne = (c) => {
    if (!c || c.id !== clientId) return c;
    return { ...c, ...patch };
  };

  const groupMemberLabel = (c) => {
    const name = c.name || c.nickname || '';
    const nickname = c.nickname && c.name && c.nickname !== c.name ? c.nickname : null;
    return nickname ? `${name} (${nickname})` : name;
  };

  const isGroupRow =
    workout.isGroup === true ||
    (Array.isArray(workout.allClients) && workout.allClients.length > 1) ||
    (Array.isArray(workout.groupClients) && workout.groupClients.length > 1);

  if (isGroupRow) {
    const source = workout.groupClients?.length ? workout.groupClients : workout.allClients;
    if (!source?.length) return workout;
    const touched = source.some((c) => c?.id === clientId);
    if (!touched) return workout;

    const next = { ...workout };
    const gc = (next.groupClients || source).map(mergeOne);
    next.groupClients = gc;
    next.allClients = (next.allClients || gc).map(mergeOne);
    next.client_avatars = next.groupClients.map((c) => c.avatar);
    next.client_nickname = next.groupClients.map(groupMemberLabel).join(', ');
    return next;
  }

  if (workout.client_id !== clientId) return workout;

  const next = { ...workout };
  if (next.allClients?.length) {
    next.allClients = next.allClients.map(mergeOne);
    const sole = next.allClients.find((c) => c.id === clientId);
    if (sole) {
      if (sole.avatar !== undefined) next.client_avatar = sole.avatar;
      if (sole.name !== undefined) next.client_name = sole.name;
      next.client_nickname = sole.name || sole.nickname || next.client_nickname || '';
    }
  } else {
    if (patch.avatar !== undefined) next.client_avatar = patch.avatar;
    if (patch.name !== undefined) next.client_name = patch.name;
    if (patch.name !== undefined || patch.nickname !== undefined) {
      next.client_nickname = patch.name || patch.nickname || next.client_nickname || '';
    }
  }
  return next;
}

const TrainerMain = ({ activeTab, refreshTrigger, onExerciseUpdate, showMonthlyCalendar, onToggleMonthlyCalendar, trainerId = null, cachedClients = null, prefetchRef = null }) => {
  const { t, locale } = useI18n();
  const dateLocale = getIntlLocaleTag(locale);

  const [allWorkouts, setAllWorkouts] = useState([]);
  const [balancesByClientId, setBalancesByClientId] = useState({});
  const { isLoading, startLoading, stopLoading, withLoading } = useLoading();

  // Функция для группировки тренировок по времени и дате
  const groupWorkoutsByTimeAndDate = (workouts) => {
    const grouped = {};
    
    workouts.forEach(workout => {
      if (workout.comment === 'Групповая тренировка') {
        const key = `${workout.date}_${workout.time}_${workout.end_time || workout.time}`;
        if (!grouped[key]) {
          grouped[key] = {
            ...workout,
            participants: []
          };
        }
        const name = workout.client_name || '';
        const nickname = workout.client_nickname || '';
        const formatted = name && nickname && name !== nickname ? `${name} (${nickname})` : (name || nickname);
        grouped[key].participants.push(formatted);
      }
    });
    
    return Object.values(grouped);
  };
  // Удаляем filteredWorkouts из state - теперь это вычисляемое значение через useMemo
  const [loading, setLoading] = useState(true);
  const [showAddWorkout, setShowAddWorkout] = useState(false);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [showWorkoutDetail, setShowWorkoutDetail] = useState(false);
  const [statusDialog, setStatusDialog] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [initialWorkoutDate, setInitialWorkoutDate] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    // Используем локальную дату, а не UTC, чтобы избежать проблем с часовыми поясами
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    // Убеждаемся, что дата валидна
    if (isNaN(today.getTime())) {
      const fallback = new Date();
      return new Date(fallback.getFullYear(), fallback.getMonth(), fallback.getDate());
    }
    return today;
  });
  const [isMobile, setIsMobile] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const abortRef = useRef(null);
  /** Latest client field patches from mad:clientUpdated — merged into cached API clients so reloads stay correct */
  const clientDisplayPatchesRef = useRef({});

  useEffect(() => {
    if (activeTab !== 'main') {
      setShowWorkoutDetail(false);
      setShowAddWorkout(false);
    }
  }, [activeTab]);

  useEffect(() => {
    const onClientUpdated = (event) => {
      const detail = event?.detail || {};
      const clientId = detail.id;
      if (!clientId) return;
      const { id: _omit, ...patch } = detail;
      if (Object.keys(patch).length === 0) return;

      clientDisplayPatchesRef.current = {
        ...clientDisplayPatchesRef.current,
        [clientId]: { ...clientDisplayPatchesRef.current[clientId], ...patch },
      };
      setAllWorkouts((prev) =>
        prev.map((w) => applyClientDisplayPatch(w, clientId, patch))
      );
    };

    window.addEventListener('mad:clientUpdated', onClientUpdated);
    return () => window.removeEventListener('mad:clientUpdated', onClientUpdated);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Определение мобильного устройства
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // При появлении сети — обновить список (подтянуть из БД после офлайн-синка)
  useEffect(() => {
    const onOnline = () => {
      if (trainerId) loadAllWorkouts();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [trainerId]);

  useEffect(() => {
    if (trainerId) {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      loadAllWorkouts(ac.signal);
    }
    return () => { abortRef.current?.abort(); };
  }, [trainerId]);

  // Refresh workouts when refreshTrigger changes (from client profile updates)
  useEffect(() => {
    if (refreshTrigger > 0) {
      loadAllWorkouts();
    }
  }, [refreshTrigger]);

  // Define getWorkoutStatus before useEffect that uses it
  const getWorkoutStatus = (workout) => {
    // Проверяем, что workout имеет необходимые свойства
    if (!workout || !workout.date || !workout.time) {
      return 'past';
    }
    
    const now = new Date();
    
    // Create workout date and time in local timezone
    const [year, month, day] = workout.date.split('-').map(Number);
    const [hours, minutes] = workout.time.split(':').map(Number);
    
    const workoutDateTime = new Date(year, month - 1, day, hours, minutes || 0, 0, 0);
    
    // Check if workout is skipped, cancelled or transferred
    if (workout.status === 'skipped' || workout.status === 'cancelled' || workout.status === 'transferred') {
      return workout.status;
    }
    
    // Check if workout is explicitly completed
    if (workout.status === 'completed') {
      return 'completed';
    }
    
    // Calculate workout end time (handle overnight: end_time < start_time means next day)
    let workoutEndTime;
    if (workout.end_time) {
      const [endHours, endMinutes] = workout.end_time.split(':').map(Number);
      workoutEndTime = new Date(year, month - 1, day, endHours, endMinutes || 0, 0, 0);
      if (workoutEndTime <= workoutDateTime) {
        workoutEndTime.setDate(workoutEndTime.getDate() + 1);
      }
    } else {
      workoutEndTime = new Date(workoutDateTime.getTime() + 60 * 60 * 1000);
    }
    
    // Check if workout is completed by time
    if (now > workoutEndTime) {
      return 'completed';
    }
    
    // Check if workout is in progress (started but not ended)
    if (now >= workoutDateTime && now <= workoutEndTime) {
      return 'in_progress';
    }
    
    // Future workout
    if (workoutDateTime > now) {
      return 'scheduled';
    }
    
    // Past workout (shouldn't reach here if logic is correct, but fallback)
    return 'completed';
  };

  // Фильтрация тренировок через useMemo - автоматически пересчитывается при изменении allWorkouts или selectedDate
  const filteredWorkouts = useMemo(() => {
    // Если нет тренировок, возвращаем пустой массив
    if (!allWorkouts || allWorkouts.length === 0) {
      return [];
    }
    
    // Проверка валидности selectedDate
    if (!selectedDate || isNaN(selectedDate.getTime())) {
      console.warn('[TrainerMain] Invalid selectedDate, returning empty array');
      return [];
    }
    
    // Получаем строку даты для сравнения (ISO формат: YYYY-MM-DD) в локальной таймзоне
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const selectedDateStr = `${year}-${month}-${day}`;
    
    // Фильтруем тренировки по дате
    // Используем startsWith для сравнения, так как workout.date может быть в формате "2025-12-10T15:00:00.000Z"
    let filtered = allWorkouts.filter(workout => {
      if (!workout || !workout.date) return false;
      // workout.date может быть в формате "2025-12-10" или "2025-12-10T15:00:00.000Z"
      // Используем startsWith для надежного сравнения
      return workout.date.startsWith(selectedDateStr);
    });

    // Скрываем только отменённые и перенесённые, если переключатель выключен; пропуски всегда показываем
    if (!showCancelled) {
      filtered = filtered.filter((workout) => {
        const status = getWorkoutStatus(workout);
        return status !== 'cancelled' && status !== 'transferred';
      });
    }
    
    return filtered.map(workout => {
      if (workout.isGroup && workout.groupClients && workout.groupClients.length > 0) {
        return {
          ...workout,
          allClients: workout.groupClients.map(c => ({
            id: c.id,
            nickname: c.nickname,
            name: c.name,
            avatar: c.avatar
          }))
        };
      }
      return {
        ...workout,
        allClients: workout.client_id
          ? [{
              id: workout.client_id,
              nickname: workout.client_nickname,
              name: workout.client_name,
              avatar: workout.client_avatar
            }]
          : []
      };
    });
  }, [allWorkouts, selectedDate, showCancelled, nowTick]);

  const selectedDayHasCancelledOrTransferred = useMemo(() => {
    if (!allWorkouts?.length || !selectedDate || isNaN(selectedDate.getTime())) {
      return false;
    }
    const y = selectedDate.getFullYear();
    const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const d = String(selectedDate.getDate()).padStart(2, '0');
    const selectedDateStr = `${y}-${m}-${d}`;
    return allWorkouts.some((w) => {
      if (!w?.date || !w.date.startsWith(selectedDateStr)) return false;
      return w.status === 'cancelled' || w.status === 'transferred';
    });
  }, [allWorkouts, selectedDate]);

  useEffect(() => {
    if (!selectedDayHasCancelledOrTransferred) {
      setShowCancelled(false);
    }
  }, [selectedDayHasCancelledOrTransferred]);

  /** «Последнее оплаченное» (оранж.): при balance > 0 — последняя будущая, покрытая балансом; при balance <= 0 — последнее завершённое со списанием. «Не оплачено» (красн.): будущие за пределами баланса + долговые. */
  const paymentWalletMeta = useMemo(() => {
    const calendarDay = (w) => {
      if (!w?.date) return '';
      const d = String(w.date);
      return d.includes('T') ? d.split('T')[0] : d.slice(0, 10);
    };

    const workoutStartMs = (w) => {
      try {
        const day = calendarDay(w);
        if (!day) return 0;
        const ms = new Date(`${day}T${w.time || '00:00'}`).getTime();
        return Number.isFinite(ms) ? ms : 0;
      } catch {
        return 0;
      }
    };

    const now = new Date(nowTick);

    const clientToWorkouts = new Map();
    for (const w of allWorkouts) {
      const ids =
        w.allClients && w.allClients.length > 0
          ? w.allClients.map((c) => c.id).filter(Boolean)
          : w.client_id
            ? [w.client_id]
            : [];
      for (const cid of ids) {
        if (!cid) continue;
        if (!clientToWorkouts.has(cid)) clientToWorkouts.set(cid, []);
        clientToWorkouts.get(cid).push(w);
      }
    }

    const unpaidByClient = new Map();
    const lastPaidByClient = new Map();

    for (const [clientId, workouts] of clientToWorkouts) {
      const balRaw = balancesByClientId[clientId];
      const balance = typeof balRaw === 'number' ? balRaw : 0;

      const debited = workouts.filter(
        (w) =>
          isWorkoutDebited(w, now) &&
          w.status !== 'cancelled' &&
          w.status !== 'transferred'
      );

      const debitedSorted = [...debited].sort((a, b) => workoutStartMs(a) - workoutStartMs(b));

      const withDeduction = debited
        .filter((w) => w.has_workout_deduction)
        .sort((a, b) => workoutStartMs(b) - workoutStartMs(a));

      if (balance < 0) {
        const set = new Set();
        // баланс = оплаты − число списанных занятий ⇒ закрыто занятий P = n + balance (FIFO с начала).
        // Не опираемся на has_workout_deduction: в БД бывают лишние workout_deductions при одной оплате — иначе 22–24 без плашки при долге.
        const n = debitedSorted.length;
        const paySlots = Math.max(0, Math.round(n + balance));
        debitedSorted.slice(paySlots).forEach((w) => set.add(w.id));
        // При долге все будущие запланированные занятия тоже «Не оплачено»
        const upcoming = workouts
          .filter(
            (w) =>
              !isWorkoutDebited(w, now) &&
              w.status !== 'cancelled' &&
              w.status !== 'transferred' &&
              w.status !== 'skipped'
          )
          .sort((a, b) => workoutStartMs(a) - workoutStartMs(b));
        upcoming.forEach((w) => set.add(w.id));
        unpaidByClient.set(clientId, set);
      }

      if (balance <= 0 && withDeduction.length > 0) {
        lastPaidByClient.set(clientId, withDeduction[0].id);
      }

      if (balance >= 0) {
        const upcoming = workouts
          .filter(
            (w) =>
              !isWorkoutDebited(w, now) &&
              w.status !== 'cancelled' &&
              w.status !== 'transferred' &&
              w.status !== 'skipped'
          )
          .sort((a, b) => workoutStartMs(a) - workoutStartMs(b));
        if (upcoming.length > 0) {
          const coveredCount = Math.min(balance, upcoming.length);
          if (coveredCount > 0) {
            lastPaidByClient.set(clientId, upcoming[coveredCount - 1].id);
          }
          const beyond = upcoming.slice(coveredCount);
          if (beyond.length > 0) {
            if (!unpaidByClient.has(clientId)) {
              unpaidByClient.set(clientId, new Set());
            }
            beyond.forEach((w) => unpaidByClient.get(clientId).add(w.id));
          }
        }
      }
    }

    return { unpaidByClient, lastPaidByClient };
  }, [allWorkouts, balancesByClientId, nowTick]);

  const getWorkoutWalletKind = useCallback(
    (workout) => {
      const clientIds =
        workout.allClients?.map((c) => c.id).filter(Boolean) ||
        (workout.client_id ? [workout.client_id] : []);

      for (const cid of clientIds) {
        if (paymentWalletMeta.unpaidByClient.get(cid)?.has(workout.id)) {
          return 'unpaid';
        }
      }
      for (const cid of clientIds) {
        if (paymentWalletMeta.lastPaidByClient.get(cid) === workout.id) {
          return 'last_paid';
        }
      }
      return null;
    },
    [paymentWalletMeta]
  );

  // Преобразует payload тренировки (API/очередь) в формат для отображения в списке
  const toDisplayWorkout = (w) => {
    if (!w || !w.date || !w.time) return null;
    const clientIds = w.client_id ? [w.client_id, ...(w.group_clients || [])].filter(Boolean) : [];
    const isGroup = clientIds.length > 1;
    return {
      ...w,
      id: w.id || `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      client_id: w.client_id,
      client_nickname: w.client_nickname || (isGroup ? t('trainer.clientsCount', { n: clientIds.length }) : '—'),
      isGroup,
      allClients: clientIds.map((id) => ({
        id,
        nickname: w.client_nickname || '—',
        name: '',
        avatar: null
      })),
      locationInfo: null,
      _pendingSync: !!(w._queued || w.queued)
    };
  };

  const loadAllWorkouts = async (signal) => {
    try {
      setLoading(true);
      
      const prefetch = prefetchRef?.current;
      if (prefetchRef) prefetchRef.current = null;

      const bearer = getApiAccessToken();
      const fetchOpts = { credentials: 'include', signal };
      if (bearer) fetchOpts.headers = { Authorization: `Bearer ${bearer}` };

      const needsFetchClients = !cachedClients || cachedClients.length === 0;
      
      const [clientsRes, locationsRes, workoutsRes] = await Promise.all([
        needsFetchClients && trainerId
          ? fetch(buildApiUrl(`/users/${trainerId}/clients`), fetchOpts).then((r) => (r.ok ? r.json() : []))
          : Promise.resolve(cachedClients || []),
        prefetch?.locations
          || (trainerId ? getTrainerLocations(trainerId).then((r) => r.data || []).catch(() => []) : Promise.resolve([])),
        prefetch?.workouts
          || fetch(trainerId ? buildApiUrl(`/workouts?trainer_id=${trainerId}`) : buildApiUrl('/workouts'), fetchOpts).then((r) => r.json())
      ]);
      
      const clients = Array.isArray(clientsRes) ? clientsRes : [];
      const locations = Array.isArray(locationsRes) ? locationsRes : [];
      const workouts = Array.isArray(workoutsRes) ? workoutsRes : [];

      const patches = clientDisplayPatchesRef.current;
      const clientsMerged = clients.map((c) => {
        const p = patches[c.id];
        return p ? { ...c, ...p } : c;
      });

      const clientsMap = new Map(clientsMerged.map((client) => [client.id, client]));
      const locationsMap = new Map(locations.map(loc => [loc.id, loc]));
      
      // Обрабатываем тренировки и добавляем данные о клиентах
      const processedWorkouts = workouts.map(workout => {
        // Добавляем информацию о локации
        const locationInfo = workout.location_id && locationsMap.has(workout.location_id) 
          ? locationsMap.get(workout.location_id)
          : null;
        // Проверяем, является ли это групповой тренировкой
        // Групповая тренировка определяется по client_ids_for_group с несколькими клиентами
        const hasGroupClients = workout.client_ids_for_group && 
          workout.client_ids_for_group.split(',').filter(Boolean).length > 1;
        
        if (hasGroupClients) {
          const clientIds = workout.client_ids_for_group.split(',').filter(Boolean);
          const groupClients = clientIds
            .map(id => clientsMap.get(id))
            .filter(Boolean);
          
          return {
            ...workout,
            isGroup: true,
            groupClients: groupClients,
            client_nickname: groupClients.map(c => {
              const name = c.name || c.nickname || '';
              const nickname = c.nickname && c.name && c.nickname !== c.name ? c.nickname : null;
              return nickname ? `${name} (${nickname})` : name;
            }).join(', '),
            client_avatars: groupClients.map(c => c.avatar),
            locationInfo: locationInfo,
            allClients: groupClients.map(c => ({
              id: c.id,
              nickname: c.nickname,
              name: c.name,
              avatar: c.avatar
            }))
          };
        } else {
          // Индивидуальная тренировка
          const client = clientsMap.get(workout.client_id);
          return {
            ...workout,
            isGroup: false,
            client_nickname: client?.name || client?.nickname || workout.client_nickname || '',
            locationInfo: locationInfo,
            client_name: client?.name || workout.client_name || '',
            client_id: workout.client_id,
            client_avatar: client?.avatar || null,
            allClients: client ? [{
              id: client.id,
              nickname: client.nickname,
              name: client.name,
              avatar: client.avatar
            }] : []
          };
        }
      });

      // Сортируем тренировки по дате и времени
      const sortedWorkouts = processedWorkouts.sort((a, b) => {
        const dateCompare = new Date(a.date) - new Date(b.date);
        if (dateCompare !== 0) return dateCompare;
        return a.time.localeCompare(b.time);
      });

      setAllWorkouts(sortedWorkouts);
      setLoading(false);

      // Load balances in the background — don't block the first paint
      if (trainerId) {
        const balOpts = { credentials: 'include' };
        if (bearer) balOpts.headers = { Authorization: `Bearer ${bearer}` };
        fetch(buildApiUrl(`/trainers/${trainerId}/clients/balances`), balOpts)
          .then(r => r.ok ? r.json() : {})
          .then(data => setBalancesByClientId(typeof data === 'object' && data !== null ? data : {}))
          .catch(() => setBalancesByClientId({}));
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Error loading workouts:', error);
      setLoading(false);
    }
  };

  const handleAddExercise = async (workoutId, exerciseData) => {
    try {
      const result = await createExercise({
        workout_id: workoutId,
        ...exerciseData
      });
      loadAllWorkouts(); // Refresh data
      return result;
    } catch (error) {
      console.error('[TrainerMain] Error adding exercise:', error);
      console.error('[TrainerMain] Error details:', {
        message: error.message,
        response: error.response?.data,
        workoutId,
        exerciseData
      });
      throw error; // Пробрасываем ошибку дальше
    }
  };

  const handleAddNote = async (workoutId, noteData) => {
    try {
      await createNote({
        workout_id: workoutId,
        ...noteData
      });
      loadAllWorkouts(); // Refresh data
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const openWorkoutStatusDialog = (workout, action) => {
    setStatusDialog({ workout, action });
  };

  const confirmWorkoutStatusChange = async () => {
    if (!statusDialog?.workout?.id || !statusDialog.action || statusUpdating) return;
    setStatusUpdating(true);
    try {
      await updateWorkout(statusDialog.workout.id, { status: statusDialog.action });
      if (statusDialog.action === 'completed') {
        toast.success(t('workout.toast.markedDone'));
      } else if (statusDialog.action === 'skipped') {
        toast.success(t('workout.markSkipBtn'));
      } else if (statusDialog.action === 'cancelled') {
        toast.success(t('workout.toast.workoutCancelled'));
      } else if (statusDialog.action === 'scheduled') {
        toast.success(t('workout.restoreSession'));
      }
      setStatusDialog(null);
      loadAllWorkouts();
    } catch (error) {
      console.error('[TrainerMain] Failed to update workout status:', error);
      const msg =
        error?.response?.data?.error ||
        error?.message ||
        t('workout.err.statusUpdate');
      toast.error(msg);
    } finally {
      setStatusUpdating(false);
    }
  };

  const statusDialogCopy = statusDialog
    ? (() => {
        switch (statusDialog.action) {
          case 'completed':
            return {
              title: t('workout.markDoneTitle'),
              description: t('workout.markDoneDesc'),
              confirm: t('workout.markDoneBtn'),
            };
          case 'skipped':
            return {
              title: t('workout.skipTitle'),
              description: t('workout.skipDesc'),
              confirm: t('workout.markSkipBtn'),
            };
          case 'cancelled':
            return {
              title: t('workout.cancelWorkoutTitle'),
              description: t('workout.cancelThisWorkout'),
              confirm: t('workout.cancelSession'),
            };
          case 'scheduled':
            return {
              title: t('workout.restoreTitle'),
              description: t('workout.restoreDesc'),
              confirm: t('workout.restore'),
            };
          default:
            return null;
        }
      })()
    : null;

  return (
    <div 
      className="w-full min-w-0 max-w-full overflow-x-hidden px-2 sm:px-4" 
      style={{ 
        paddingTop: 'var(--space-md)', 
        paddingBottom: 'var(--space-md)'
      }}
    >
      {/* Horizontal Date Picker - only show when not in calendar view */}
      {!showMonthlyCalendar && (
        <div style={{ marginBottom: 'var(--space-md)' }}>
          <HorizontalDatePicker
            selectedDate={selectedDate}
            onDateChange={(date) => {
              if (date && !isNaN(date.getTime())) {
                setSelectedDate(date);
              }
            }}
          />
        </div>
      )}

      {/* Main content */}
      {loading ? (
        <LoadingOverlay isLoading={true} text={t('trainer.loadingWorkouts')}>
          <div className="space-y-4">
            <LoadingCard lines={3} />
            <LoadingCard lines={2} />
            <LoadingCard lines={4} />
          </div>
        </LoadingOverlay>
      ) : showMonthlyCalendar ? (
        /* Weekly Calendar View */
        <ClassicWeekPlannerCompact
          workouts={allWorkouts}
          balancesByClientId={balancesByClientId}
          onWorkoutClick={(workout) => {
            setSelectedWorkout(workout);
            setShowWorkoutDetail(true);
          }}
          onAddWorkout={(date) => {
            if (date instanceof Date) {
              setSelectedDate(date);
              setInitialWorkoutDate(date);
            } else {
              setInitialWorkoutDate(date);
            }
            setShowAddWorkout(true);
          }}
        />
      ) : (
        /* Workouts List */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
          <Card className="shadow-sm border-0 bg-background">
            <CardHeader className="pb-3 px-4 pt-4">
              <CardTitle className="text-base font-semibold">
                <div className="flex items-center" style={{ gap: 'var(--space-sm)' }}>
                  <Dumbbell className="h-5 w-5" />
                  {selectedDate
                    ? t('trainer.workoutsOn', {
                        date: selectedDate.toLocaleDateString(dateLocale, {
                          day: 'numeric',
                          month: 'long',
                        }),
                      })
                    : t('trainer.allWorkouts')}
                </div>
              </CardTitle>
              {selectedDayHasCancelledOrTransferred && (
                <div className="flex items-center justify-end mt-3" style={{ gap: 'var(--space-sm)' }}>
                  <Checkbox
                    id="show-cancelled"
                    checked={showCancelled}
                    onCheckedChange={(v) => setShowCancelled(v === true)}
                    className="flex-shrink-0 h-5 w-5 touch-manipulation"
                  />
                  <Label htmlFor="show-cancelled" className="text-sm font-medium cursor-pointer whitespace-nowrap touch-manipulation" style={{ minHeight: '44px', display: 'flex', alignItems: 'center' }}>
                    {t('trainer.showCancelled')}
                  </Label>
                </div>
              )}
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {filteredWorkouts.length === 0 ? (
                <Empty>
                  <EmptyIcon>
                    <Dumbbell className="h-6 w-6" />
                  </EmptyIcon>
                  <EmptyTitle>{t('trainer.noWorkouts')}</EmptyTitle>
                  <EmptyDescription>
                    {t('trainer.noWorkoutsHint')}
                  </EmptyDescription>
                  <EmptyAction>
                    <Button 
                      variant="outline" 
                      onClick={() => setShowAddWorkout(true)}
                      className="touch-manipulation"
                      style={{ marginTop: 'var(--space-lg)', minHeight: '44px' }}
                    >
                      <Plus className="h-4 w-4" style={{ marginRight: 'var(--space-sm)' }} />
                      {t('trainer.addWorkout')}
                    </Button>
                  </EmptyAction>
                </Empty>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                  {filteredWorkouts.map((workout) => {
                    const status = getWorkoutStatus(workout);
                    const walletKind = getWorkoutWalletKind(workout);

                    const getStatusText = () => {
                      switch (status) {
                        case 'in_progress':
                          return t('payments.status.in_progress');
                        case 'scheduled':
                          return t('payments.status.scheduled');
                        case 'completed':
                          return t('payments.status.completed');
                        case 'past_day':
                          return t('payments.status.past_day');
                        case 'skipped':
                          return t('payments.status.skipped');
                        case 'cancelled':
                          return t('payments.status.cancelled');
                        case 'transferred':
                          return t('payments.status.transferred');
                        default:
                          return t('payments.status.workout');
                      }
                    };

                    const getBadgeVariant = () => {
                      switch (status) {
                        case 'in_progress': return 'default';
                        case 'scheduled': return 'default';
                        case 'completed': return 'default';
                        case 'past_day': return 'default';
                        case 'skipped': return 'destructive';
                        case 'cancelled': return 'destructive';
                        case 'transferred': return 'destructive';
                        default: return 'default';
                      }
                    };

                    const isCancelledOrTransferred = status === 'cancelled' || status === 'transferred' || status === 'skipped';

                    return (
                      <Card 
                        key={workout.id} 
                        className="cursor-pointer active:shadow-md transition-all touch-manipulation rounded-xl shadow-sm border border-border/50"
                        onClick={() => {
                          setSelectedWorkout(workout);
                          setShowWorkoutDetail(true);
                        }}
                        style={{ 
                          minHeight: '80px',
                          position: 'relative'
                        }}
                      >
                        <CardContent style={{ padding: 'var(--space-md)', position: 'relative' }}>
                          {/* Badge статуса - фиксирован в правом верхнем углу */}
                          <div 
                            className="absolute top-0 right-0 flex items-center flex-shrink-0 gap-1" 
                            style={{ 
                              top: 'var(--space-md)',
                              right: 'var(--space-md)',
                              zIndex: 10
                            }}
                          >
                            <Badge 
                              variant={getBadgeVariant()}
                              className={`text-xs font-medium px-2 py-1 whitespace-nowrap ${
                                status === 'completed' || status === 'past_day'
                                  ? 'bg-gray-500 text-white border-transparent hover:bg-gray-600'
                                  : status === 'in_progress'
                                  ? 'bg-green-100 text-green-800 border-green-300'
                                  : ''
                              }`}
                              style={{
                                fontSize: '0.75rem',
                                lineHeight: '1.25rem',
                                padding: '0.25rem 0.5rem'
                              }}
                            >
                              {getStatusText()}
                            </Badge>
                            {workout.status !== 'transferred' ? (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button
                                    type="button"
                                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label={t('workout.statusMenuAria')}
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                  {workout.status === 'cancelled' || workout.status === 'skipped' ? (
                                    <DropdownMenuItem
                                      onSelect={() => openWorkoutStatusDialog(workout, 'scheduled')}
                                    >
                                      {t('workout.restoreSession')}
                                    </DropdownMenuItem>
                                  ) : (
                                    <>
                                      {workout.status !== 'completed' ? (
                                        <DropdownMenuItem
                                          onSelect={() => openWorkoutStatusDialog(workout, 'completed')}
                                        >
                                          {t('workout.markDoneBtn')}
                                        </DropdownMenuItem>
                                      ) : null}
                                      <DropdownMenuItem
                                        className="text-destructive focus:text-destructive"
                                        onSelect={() => openWorkoutStatusDialog(workout, 'skipped')}
                                      >
                                        {t('workout.markSkipBtn')}
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        className="text-destructive focus:text-destructive"
                                        onSelect={() => openWorkoutStatusDialog(workout, 'cancelled')}
                                      >
                                        {t('workout.cancelSession')}
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ) : null}
                          </div>

                          {/* Основной контент с отступом справа для badge */}
                          <div 
                            className="flex items-start gap-3"
                            style={{ 
                              paddingRight: 'calc(112px + var(--space-sm))',
                              minWidth: 0
                            }}
                          >
                            {/* Avatars - Multiple for group workouts */}
                            {workout.allClients && workout.allClients.length > 1 ? (
                              <div className="flex -space-x-2 flex-shrink-0" style={{ minWidth: '48px' }}>
                                {workout.allClients.slice(0, 3).map((client, idx) => (
                                  <Avatar 
                                    key={client.id || idx} 
                                    className="h-12 w-12 border-2 border-background"
                                    style={{ width: '3rem', height: '3rem', flexShrink: 0 }}
                                  >
                                    <AvatarImage src={client.avatar} alt={client.name || client.nickname} />
                                    <AvatarFallback className="bg-primary text-primary-foreground text-base font-medium">
                                      {(client.name || client.nickname)?.charAt(0)?.toUpperCase() || '?'}
                                    </AvatarFallback>
                                  </Avatar>
                                ))}
                                {workout.allClients.length > 3 && (
                                  <div 
                                    className="rounded-full bg-muted border-2 border-background flex items-center justify-center text-xs font-medium"
                                    style={{ 
                                      width: '3rem', 
                                      height: '3rem',
                                      flexShrink: 0
                                    }}
                                  >
                                    +{workout.allClients.length - 3}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <Avatar 
                                className="flex-shrink-0"
                                style={{ width: '3rem', height: '3rem' }}
                              >
                                <AvatarImage src={workout.client_avatar || (workout.allClients?.[0]?.avatar)} alt={workout.client_nickname || workout.client_name || workout.allClients?.[0]?.nickname} />
                                <AvatarFallback className="bg-primary text-primary-foreground text-base font-medium">
                                  {workout.client_nickname === 'Самотрен'
                                    ? (t('trainer.selfTraining').charAt(0) || 'S')
                                    : (workout.client_nickname || workout.client_name || workout.allClients?.[0]?.nickname)
                                        ?.charAt(0)
                                        ?.toUpperCase() || '?'}
                                </AvatarFallback>
                              </Avatar>
                            )}
                            
                            <div className="flex-1 min-w-0" style={{ overflow: 'hidden' }}>
                              <div className="flex items-center gap-1.5 flex-wrap mb-1 min-w-0">
                                <h3 
                                  className={`font-semibold m-0 ${isCancelledOrTransferred ? 'line-through' : ''}`}
                                  style={{ 
                                    fontSize: '1rem',
                                    lineHeight: '1.5rem',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word'
                                  }}
                                >
                                  {workout.allClients && workout.allClients.length > 1
                                    ? workout.allClients.map(c => c.name || c.nickname || '').filter(Boolean).join(', ')
                                    : workout.client_nickname === 'Самотрен'
                                      ? t('trainer.selfTraining')
                                      : workout.client_name ||
                                        workout.allClients?.[0]?.name ||
                                        workout.client_nickname ||
                                        workout.allClients?.[0]?.nickname ||
                                        t('trainer.clientFallback')}
                                </h3>
                                {walletKind && (
                                  <Popover modal={isMobile}>
                                    <PopoverTrigger asChild>
                                      <button
                                        type="button"
                                        onClick={(e) => e.stopPropagation()}
                                        className={cn(
                                          'inline-flex shrink-0 items-center justify-center rounded-md px-1.5 py-1 shadow-sm ring-1 touch-manipulation',
                                          walletKind === 'unpaid'
                                            ? 'bg-red-600 ring-red-700/30'
                                            : 'bg-orange-500 ring-orange-600/40'
                                        )}
                                        aria-label={
                                          walletKind === 'unpaid'
                                            ? t('trainer.walletUnpaid')
                                            : t('trainer.walletLastPaid')
                                        }
                                      >
                                        <Wallet className="h-3.5 w-3.5 text-white" strokeWidth={2} aria-hidden />
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                      className="w-auto max-w-[min(280px,90vw)] p-3 text-sm"
                                      align="start"
                                      sideOffset={6}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {walletKind === 'unpaid'
                                        ? t('trainer.walletUnpaid')
                                        : t('trainer.walletLastPaid')}
                                    </PopoverContent>
                                  </Popover>
                                )}
                              </div>
                              <p 
                                className={`text-muted-foreground mb-1 ${isCancelledOrTransferred ? 'line-through' : ''}`}
                                style={{ 
                                  fontSize: '0.875rem',
                                  lineHeight: '1.25rem'
                                }}
                              >
                                {workout.time} - {workout.end_time || workout.time}
                              </p>
                              {/* Отображение локации */}
                              {workout.location_type === 'online' && (
                                <p 
                                  className={`text-muted-foreground text-sm ${isCancelledOrTransferred ? 'line-through' : ''}`}
                                  style={{ 
                                    fontSize: '0.75rem',
                                    lineHeight: '1.25rem',
                                    marginTop: '0.25rem'
                                  }}
                                >
                                  💻 {t('trainer.online')}
                                </p>
                              )}
                              {workout.location_type === 'offline' && workout.location_id && workout.locationInfo && (
                                <p 
                                  className={`text-muted-foreground text-sm ${isCancelledOrTransferred ? 'line-through' : ''}`}
                                  style={{ 
                                    fontSize: '0.75rem',
                                    lineHeight: '1.25rem',
                                    marginTop: '0.25rem'
                                  }}
                                >
                                  📍 {workout.locationInfo.name}{workout.locationInfo.address ? `, ${workout.locationInfo.address}` : ''}
                                </p>
                              )}
                              {/* Для обратной совместимости: если есть location, но нет location_type */}
                              {!workout.location_type && (workout.location === 'Онлайн' || workout.location === 'online') && (
                                <p 
                                  className={`text-muted-foreground text-sm ${isCancelledOrTransferred ? 'line-through' : ''}`}
                                  style={{ 
                                    fontSize: '0.75rem',
                                    lineHeight: '1.25rem',
                                    marginTop: '0.25rem'
                                  }}
                                >
                                  💻 {t('trainer.online')}
                                </p>
                              )}
                              {!workout.location_type && workout.location && workout.location !== 'Онлайн' && workout.location !== 'online' && (
                                <p 
                                  className={`text-muted-foreground text-sm ${isCancelledOrTransferred ? 'line-through' : ''}`}
                                  style={{ 
                                    fontSize: '0.75rem',
                                    lineHeight: '1.25rem',
                                    marginTop: '0.25rem'
                                  }}
                                >
                                  📍 {workout.location}
                                </p>
                              )}
                              {workout.comment && (
                                <p 
                                  className={`text-muted-foreground ${isCancelledOrTransferred ? 'line-through' : ''}`}
                                  style={{ 
                                    fontSize: '0.75rem',
                                    lineHeight: '1.25rem',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    marginTop: 'var(--space-xs)'
                                  }}
                                >
                                  {workout.comment}
                                </p>
                              )}
                              {workout.allClients && workout.allClients.length > 1 && (
                                <div 
                                  className={`text-muted-foreground ${isCancelledOrTransferred ? 'line-through' : ''}`}
                                  style={{ 
                                    fontSize: '0.75rem',
                                    lineHeight: '1.25rem',
                                    marginTop: 'var(--space-xs)'
                                  }}
                                >
                                  <div className="flex items-center flex-wrap" style={{ gap: 'var(--space-xs)' }}>
                                    <span className="text-blue-600 font-medium">{t('trainer.participants')}</span>
                                    {workout.allClients.map((client, index) => (
                                      <span key={client.id || index} className="inline-flex items-center" style={{ gap: 'var(--space-xs)' }}>
                                        <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                                        <span style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{client.name || client.nickname}</span>
                                        {index < workout.allClients.length - 1 && <span>,</span>}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Floating Add Button - Mobile optimized */}
      <Button
        onClick={() => setShowAddWorkout(true)}
        className="fixed rounded-full shadow-lg active:shadow-xl transition-all duration-200 z-40 bg-primary active:bg-primary/90 touch-manipulation"
        style={{ 
          bottom: 'calc(var(--app-tab-bar-height) + var(--space-lg))', 
          right: 'var(--space-md)',
          height: '56px',
          width: '56px',
          minHeight: '56px',
          minWidth: '56px'
        }}
        size="icon"
      >
        <Plus className="h-7 w-7 text-white" />
      </Button>

      <OptimizedWorkoutForm
        show={showAddWorkout}
        onHide={() => {
          // Закрываем модальное окно немедленно
          setShowAddWorkout(false);
          setInitialWorkoutDate(null);
          // Загружаем тренировки асинхронно, чтобы не блокировать UI
          setTimeout(() => {
            loadAllWorkouts();
          }, 0);
        }}
        onSave={(workout) => {
          setShowAddWorkout(false);
          setInitialWorkoutDate(null);
          // Оптимистичное отображение: сразу добавляем в список
          if (workout) {
            const toAdd = Array.isArray(workout)
              ? workout.map(toDisplayWorkout).filter(Boolean)
              : [toDisplayWorkout(workout)].filter(Boolean);
            if (toAdd.length > 0) {
              setAllWorkouts((prev) => {
                const merged = [...prev];
                for (const w of toAdd) {
                  if (w.id && !merged.some((x) => x.id === w.id)) {
                    merged.push(w);
                  }
                }
                return merged.sort((a, b) => {
                  const dc = new Date(a.date) - new Date(b.date);
                  return dc !== 0 ? dc : (a.time || '').localeCompare(b.time || '');
                });
              });
            }
          }
          loadAllWorkouts();
        }}
        trainerId={trainerId}
        initialDate={initialWorkoutDate}
      />

      {showWorkoutDetail && (
        <Suspense fallback={null}>
          <WorkoutDetailModal
            show={showWorkoutDetail}
            onHide={() => setShowWorkoutDetail(false)}
            workout={selectedWorkout}
            trainerId={trainerId}
            onOpenWorkout={(w) => setSelectedWorkout(w)}
            onAddExercise={handleAddExercise}
            onAddNote={handleAddNote}
            onWorkoutUpdate={loadAllWorkouts}
            onExerciseUpdate={onExerciseUpdate}
          />
        </Suspense>
      )}

      <AlertDialog open={!!statusDialog} onOpenChange={(open) => !open && !statusUpdating && setStatusDialog(null)}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>{statusDialogCopy?.title}</AlertDialogTitle>
            <AlertDialogDescription>{statusDialogCopy?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={statusUpdating}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={statusUpdating}
              onClick={(e) => {
                e.preventDefault();
                confirmWorkoutStatusChange();
              }}
              className={
                statusDialog?.action === 'cancelled' || statusDialog?.action === 'skipped'
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : undefined
              }
            >
              {statusUpdating ? t('common.saving') : statusDialogCopy?.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TrainerMain;